import { create } from 'zustand';
import { User } from '@/types/auth';
import { supabase } from '@/lib/supabase/client';

interface WorkerState {
  workers: User[];
  isLoading: boolean;
  error: string | null;
  fetchWorkers: () => Promise<void>;
  addWorker: (worker: User) => void;
  updateWorker: (workerId: string, data: Partial<User>) => Promise<void>;
  deleteWorker: (workerId: string) => Promise<void>;
  initialize: () => () => void;
}

export const useWorkerStore = create<WorkerState>((set, get) => ({
  workers: [],
  isLoading: false,
  error: null,

  fetchWorkers: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('role', 'worker')
        .or('status.is.null,status.eq.active') // Correct OR syntax for Supabase
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ workers: data || [] });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to fetch workers' });
    } finally {
      set({ isLoading: false });
    }
  },

  addWorker: (worker: User) => {
    set(state => ({
      workers: [worker, ...state.workers]
    }));
  },

  updateWorker: async (workerId: string, data: Partial<User>) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('users')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', workerId)
        .eq('role', 'worker');

      if (error) throw error;

      // Update local state immediately
      set(state => ({
        workers: state.workers.map(worker =>
          worker.id === workerId ? { ...worker, ...data } : worker
        )
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to update worker' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteWorker: async (workerId: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // Update status to inactive (soft delete) instead of deleting
      const { error: dbError } = await supabase
        .from('users')
        .update({ 
          status: 'inactive',
          updated_at: new Date().toISOString()
        })
        .eq('id', workerId)
        .eq('role', 'worker');

      if (dbError) throw dbError;

      // No longer deleting the auth user to prevent conflicts
      
      // Update local state to filter out the inactive worker
      set(state => ({
        workers: state.workers.filter(worker => worker.id !== workerId)
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to delete worker' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  initialize: () => {
    // Setup realtime subscription only — initial fetch is handled by the page component
    // on mount, when the auth session is guaranteed to be restored
    const channel = supabase.channel('workers_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'users',
          filter: 'role=eq.worker'
        },
        async () => {
          await get().fetchWorkers();
        }
      )
      .subscribe();

    // Return cleanup function
    return () => {
      supabase.removeChannel(channel);
    };
  }
}));

// Initialize the store
useWorkerStore.getState().initialize();
